// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { PRICING_MODE, PricingMode } from '@/constants/pricing-mode';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { toSerializableCurrency } from '@/currency/utilities/to-form-value';
import { ItemPricing } from '@/items/api/types';

interface InvoiceLinePricingFields {
  pricingMode: PricingMode;
  unitPrice: string | null;
  totalPrice: string;
}

// Applies the SKU catalog default's pricing mode to an invoice/charge line, and
// its amount ONLY when the default shares the charge currency. A foreign-currency
// default (e.g. a USD catalog price on a CAD charge) contributes its mode but not
// its number — we never copy a foreign amount into a different denomination.
export function applyInvoiceLineItemPricingDefault(
  current: InvoiceLinePricingFields,
  defaultPricing: ItemPricing | null,
  chargeCurrencyCode: IsoCurrencyCode
): InvoiceLinePricingFields {
  if (defaultPricing === null) {
    return current;
  }

  if (defaultPricing.pricingMode === PRICING_MODE.PER_UNIT) {
    return {
      pricingMode: PRICING_MODE.PER_UNIT,
      unitPrice:
        defaultPricing.unitPrice &&
        defaultPricing.unitPrice.isoCode === chargeCurrencyCode
          ? toSerializableCurrency(defaultPricing.unitPrice).amount
          : current.unitPrice,
      totalPrice: '',
    };
  }

  if (defaultPricing.pricingMode === PRICING_MODE.FIXED) {
    return {
      pricingMode: PRICING_MODE.FIXED,
      unitPrice: null,
      totalPrice:
        defaultPricing.fixedPrice &&
        defaultPricing.fixedPrice.isoCode === chargeCurrencyCode
          ? toSerializableCurrency(defaultPricing.fixedPrice).amount
          : current.totalPrice,
    };
  }

  return current;
}
